import React, { useCallback, useEffect, useState } from 'react';
import { SpotInterface } from 'types';
import { InsightsPeriod, PropertyInsightsSummary, TenantInsightsPeriod } from 'types/PropertyInsights';
import { HTTPError } from 'clients/HTTPClient';
import { propertyInsightsClient } from 'clients';
import WidgetBox from 'components/widgets/WidgetBox';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import Loader from 'components/Loader/Loader';
import StarIcon from 'components/Icons/StarIcon';
import ArrowIcon from 'components/Icons/ArrowIcon';
import {
    formatCount,
    insightsPeriodLabels,
    FREQUENT_COLOR,
    ONE_TIME_COLOR,
    RETURN_COLOR,
    TrendPeriod,
} from '../helpers';
import { driverDestinationCategoryIcon } from '../DriverDestinations/DriverDestinations';
import reviewsStyles from '../DriverReviewsWidget/DriverReviewsWidget.module.css';
import destinationsStyles from '../DriverDestinations/DriverDestinations.module.css';
import transientDriversStyles from '../TransientDriversWidget/TransientDriversWidget.module.css';
import styles from './PropertyInsightsWidget.module.css';

type PropertyInsightsWidgetProps = {
    spot: SpotInterface;
    navigate: (path: string) => void;
};

const PERIOD_LABEL = insightsPeriodLabels[InsightsPeriod.SIX_MONTHS];
const urlParamsWith6MPeriod = `?trendPeriod=${TrendPeriod.SIX}&reviewsPeriod=${InsightsPeriod.SIX_MONTHS}&transientDriversPeriod=${InsightsPeriod.SIX_MONTHS}&tenantUsagePeriod=${TenantInsightsPeriod.SIX_MONTHS}&peakTimesPeriod=${InsightsPeriod.SIX_MONTHS}`;

function PropertyInsightsWidget({ spot, navigate }: PropertyInsightsWidgetProps): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [summary, setSummary] = useState<PropertyInsightsSummary | null>(null);

    const getPropertyInsightsSummary = useCallback(async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const response = await propertyInsightsClient.getPropertyInsightsSummary(spot.uuid);
            setSummary(response);
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [spot.uuid]);

    useEffect(() => {
        getPropertyInsightsSummary();
    }, [spot.uuid, getPropertyInsightsSummary]);

    const isLoading = loading || !summary;

    return (
        <WidgetBox
            title="Property Insights"
            rightElement={
                <button
                    onClick={() => navigate('/property-insights' + urlParamsWith6MPeriod)}
                    className={styles.seeMoreButton}
                >
                    See More <ArrowIcon direction="right" />
                </button>
            }
        >
            {errorMessage ? (
                <WidgetErrorMessage message={errorMessage} onRetry={getPropertyInsightsSummary} />
            ) : (
                <div>
                    {isLoading ? (
                        <Loader centered />
                    ) : (
                        <div className={styles.sectionsContainer}>
                            {/* Drivers Reviews */}
                            {summary.reviews && (
                                <div>
                                    <div className={styles.sectionHeader}>
                                        <p>Drivers Reviews</p>
                                        <span>{PERIOD_LABEL}</span>
                                    </div>
                                    <div className={reviewsStyles.horizontalSummary}>
                                        <div className={reviewsStyles.summaryRating}>
                                            <StarIcon /> <h2>{summary.reviews.avg_rating}</h2>
                                        </div>
                                        <p className={reviewsStyles.summaryTotalReviews} style={{ fontSize: '14px' }}>
                                            {formatCount(summary.reviews.total_reviews)}
                                        </p>
                                        <span className={reviewsStyles.summaryPeriod} style={{ fontSize: '12px' }}>
                                            reviews in the {PERIOD_LABEL}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Driver Destinations */}
                            {summary.driver_destination && summary.driver_destination.length > 0 && (
                                <div>
                                    <div className={styles.sectionHeader}>
                                        <p>Top 3 Driver Destinations</p>
                                    </div>
                                    <div className={styles.destinations}>
                                        {summary.driver_destination.map((destination) => {
                                            const { icon, background } =
                                                driverDestinationCategoryIcon[destination.destination_category];
                                            const { destination_category: category, category_percentage: percentage } =
                                                destination;
                                            return (
                                                <div
                                                    key={destination.destination_category}
                                                    className={styles.destination}
                                                >
                                                    <div
                                                        className={destinationsStyles.destinationIcon}
                                                        style={{ background }}
                                                    >
                                                        {icon}
                                                    </div>
                                                    <div>
                                                        <p className={styles.destinationCategoryName}>{category}</p>
                                                        <p className={styles.destinationPercentage}>{percentage}%</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Transient Drivers */}
                            {summary.transient_driver && (
                                <>
                                    <div>
                                        <div className={styles.sectionHeader}>
                                            <p>Transient Drivers</p>
                                            <span>{PERIOD_LABEL}</span>
                                        </div>
                                        <div className={styles.transientsBars}>
                                            <div
                                                style={{
                                                    width: `${summary.transient_driver.one_time_pct}%`,
                                                    backgroundColor: ONE_TIME_COLOR,
                                                    display:
                                                        summary.transient_driver.one_time_pct > 0 ? 'inherit' : 'none',
                                                }}
                                            />
                                            <div
                                                style={{
                                                    width: `${summary.transient_driver.return_pct}%`,
                                                    backgroundColor: RETURN_COLOR,
                                                    display:
                                                        summary.transient_driver.return_pct > 0 ? 'inherit' : 'none',
                                                }}
                                            />
                                            <div
                                                style={{
                                                    width: `${summary.transient_driver.frequent_pct}%`,
                                                    backgroundColor: FREQUENT_COLOR,
                                                    display:
                                                        summary.transient_driver.frequent_pct > 0 ? 'inherit' : 'none',
                                                }}
                                            />
                                        </div>

                                        <div className={transientDriversStyles.breakdownInfoContainer}>
                                            <div className={transientDriversStyles.breakdownInfoItem}>
                                                <div className={transientDriversStyles.infoTitle}>
                                                    <div
                                                        className={transientDriversStyles.infoColorRef}
                                                        style={{ backgroundColor: ONE_TIME_COLOR }}
                                                    />
                                                    <p className={transientDriversStyles.infoLabel}>One-time</p>
                                                </div>
                                                <p className={transientDriversStyles.infoPct}>
                                                    {summary.transient_driver.one_time_pct}%
                                                </p>
                                            </div>

                                            <div className={transientDriversStyles.breakdownInfoItem}>
                                                <div className={transientDriversStyles.infoTitle}>
                                                    <div
                                                        className={transientDriversStyles.infoColorRef}
                                                        style={{ backgroundColor: RETURN_COLOR }}
                                                    />
                                                    <p className={transientDriversStyles.infoLabel}>Return</p>
                                                </div>
                                                <p className={transientDriversStyles.infoPct}>
                                                    {summary.transient_driver.return_pct}%
                                                </p>
                                            </div>

                                            <div className={transientDriversStyles.breakdownInfoItem}>
                                                <div className={transientDriversStyles.infoTitle}>
                                                    <div
                                                        className={transientDriversStyles.infoColorRef}
                                                        style={{ backgroundColor: FREQUENT_COLOR }}
                                                    />
                                                    <p className={transientDriversStyles.infoLabel}>Frequent</p>
                                                </div>
                                                <p className={transientDriversStyles.infoPct}>
                                                    {summary.transient_driver.frequent_pct}%
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Transients (per day subsection) */}
                                    <div>
                                        <div className={styles.transientsPerDay}>
                                            <div>
                                                <p className={transientDriversStyles.highlightLabel}>
                                                    Repeat Drivers per Day
                                                </p>
                                                <p className={transientDriversStyles.highlightValue}>
                                                    {formatCount(summary.transient_driver.repeat_drivers_per_day)}
                                                </p>
                                            </div>

                                            <div>
                                                <p className={transientDriversStyles.highlightLabel}>
                                                    Drivers Who Parked Once
                                                </p>
                                                <p className={transientDriversStyles.highlightValue}>
                                                    {formatCount(summary.transient_driver.non_returning_drivers)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
        </WidgetBox>
    );
}

export default PropertyInsightsWidget;
